import { useMemo, useState } from 'react';
import { useTutors } from '../api/sessions';
import { Avatar } from './Avatar';
import { Spinner } from './Spinner';
import { useDebounced } from '../hooks/useDebounced';

/**
 * Picks a tutor by searching, rather than a `<select>` of everyone.
 *
 * A dropdown is fine at twenty tutors and unusable at two hundred, and the
 * admin already knows who they are looking for — the hard part is confirming
 * the rating and the subjects before committing a family to them.
 */
export function TutorPicker({
  value,
  onChange,
  subjectSlug,
  excludeTutorId,
  label = 'Tutor',
}: {
  value?: string;
  onChange: (tutorId: string | undefined, fullName?: string) => void;
  /** Narrows the list to people who teach what the lesson is in. */
  subjectSlug?: string;
  /** The tutor already on this record — swapping to them is refused. */
  excludeTutorId?: string;
  label?: string;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);

  const query = useMemo(
    () => ({ limit: 8, q: debounced.trim() || undefined, subject: subjectSlug }),
    [debounced, subjectSlug],
  );
  const tutors = useTutors(query);

  const options = (tutors.data?.data ?? []).filter((t) => t.id !== excludeTutorId);

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-ink-2" htmlFor="tutor-search">
        {label}
        {subjectSlug && (
          <span className="ml-1 font-normal text-ink-3">
            · only people who teach {subjectSlug.replace(/_/g, ' ')}
          </span>
        )}
      </label>
      <input
        id="tutor-search"
        className="input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name"
        autoComplete="off"
      />

      <div className="max-h-56 overflow-y-auto rounded-md border border-rule">
        {tutors.isLoading ? (
          <div className="flex items-center gap-2 p-3 text-xs text-ink-3">
            <Spinner /> Finding tutors…
          </div>
        ) : options.length === 0 ? (
          <p className="p-3 text-xs text-ink-3">
            {subjectSlug
              ? 'Nobody matching teaches that subject. Clear the search, or widen the subject.'
              : 'No tutors match that search.'}
          </p>
        ) : (
          <ul>
            {options.map((t) => {
              const selected = t.id === value;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onChange(selected ? undefined : t.id, t.fullName)}
                    aria-pressed={selected}
                    className={`flex w-full items-center gap-2 border-b border-rule px-3 py-2 text-left last:border-b-0 ${
                      selected ? 'bg-accent-wash' : 'hover:bg-surface-2'
                    }`}
                  >
                    <Avatar name={t.fullName} src={t.avatarUrl} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{t.fullName}</span>
                      <span className="block truncate text-xs text-ink-3">
                        {t.headline ?? 'No headline'}
                      </span>
                    </span>
                    <span className="whitespace-nowrap text-xs text-ink-3">
                      {t.ratingCount > 0 ? `${t.ratingAvg.toFixed(1)} ★` : 'No ratings'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
